import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Loader2, ChevronDown, FileText, CheckCircle2, Clock, User as UserIcon, Download, Plus, Trash2, Edit2, X, Check, Save, AlertTriangle, History, Eye, MessageCircle, Paperclip, Mail, Phone } from 'lucide-react'
import { useWorkOrder } from '@hooks/useWorkOrders'
import { WorkOrderStatus, WorkOrderServiceItem, WorkOrderPieceItem } from '../types/workOrder'
import { cn } from '@utils/cn'
import { AIChat } from '@components/chat/AIChat'
import { useAuthStore } from '@store/useAuthStore'
import { useEntryLevel } from '@hooks/useEntryLevel'
import { useMemo, useState, useEffect, useRef } from 'react'
import { workOrderService } from '@services/workOrderService'
import { workOrderAttachmentService, WorkOrderAttachment } from '@services/workOrderAttachmentService'
import { costService, Cost } from '@services/costService'
import { useQuery, useQueryClient } from 'react-query'
import { FormattedObservations } from '@utils/formatObservations'
import { MechanicObservationsSections } from '@components/MechanicObservationsSections'
import { notificationService } from '@services/notificationService'
import { userService, User } from '@services/userService'
import { Select } from '@components/ui/Select'
import { CatalogAutocompleteInput } from '@components/ui/CatalogAutocompleteInput'
import { CollapsibleSection } from '@components/ui/CollapsibleSection'
import { AttachmentMedia } from '@components/AttachmentMedia'
import {
  VehicleRevisionPlanPanel,
  type VehicleRevisionPlanPanelHandle,
} from '@components/VehicleRevisionPlanPanel'
import { extractKmFromWorkOrderObservations } from '@utils/workOrderObservations'
import { vehicleService } from '@services/vehicleService'
import { invalidateNotifications } from '@utils/notificationQueries'
import { TECHNICAL_TEAM_ERROR_MESSAGE } from '@constants/userFacingErrors'
import { handleApiError } from '@services/apiErrorHandler'

const formatStatus = (status: WorkOrderStatus): string => {
  const statusMap: Record<WorkOrderStatus, string> = {
    [WorkOrderStatus.SCHEDULED]: 'Agendado',
    [WorkOrderStatus.PENDING]: 'Pendente',
    [WorkOrderStatus.DRAFT]: 'Rascunho',
    [WorkOrderStatus.IN_PROGRESS]: 'Em Andamento',
    [WorkOrderStatus.WAITING_PARTS]: 'Aguarda Peças',
    [WorkOrderStatus.WAITING_APPROVAL]: 'Aguarda Aprovação',
    [WorkOrderStatus.RESOLVED]: 'Resolvida',
    [WorkOrderStatus.COMPLETED]: 'Concluída',
    [WorkOrderStatus.CANCELLED]: 'Cancelada',
    [WorkOrderStatus.INVOICED]: 'Faturada',
  }
  return statusMap[status] || status
}

/** Apenas cancelada ou faturada bloqueiam edição; concluída continua editável. */
const isWorkOrderEditingLocked = (status?: WorkOrderStatus): boolean =>
  status === WorkOrderStatus.CANCELLED || status === WorkOrderStatus.INVOICED

const WORK_ORDER_WORKFLOW_STATUSES: WorkOrderStatus[] = [
  WorkOrderStatus.PENDING,
  WorkOrderStatus.DRAFT,
  WorkOrderStatus.IN_PROGRESS,
  WorkOrderStatus.WAITING_PARTS,
  WorkOrderStatus.WAITING_APPROVAL,
  WorkOrderStatus.RESOLVED,
  WorkOrderStatus.COMPLETED,
]

const formatDate = (dateString?: string): string => {
  if (!dateString) return 'N/A'
  try {
    const date = new Date(dateString)
    return new Intl.DateTimeFormat('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date)
  } catch {
    return dateString
  }
}

// Função para capitalizar primeira letra (mantém o resto como digitado)
const capitalizeFirstLetter = (text: string): string => {
  if (!text || text.length === 0) return text
  // Se já começa com maiúscula, não altera
  if (text.charAt(0) === text.charAt(0).toUpperCase() && text.charAt(0).match(/[A-ZÀÁÂÃÉÊÍÓÔÕÚÇ]/)) {
    return text
  }
  // Capitaliza apenas a primeira letra, mantém o resto
  return text.charAt(0).toUpperCase() + text.slice(1)
}
  
// Função para normalizar preço (remove zeros à esquerda/direita, converte vírgula para ponto)
const normalizePrice = (value: string | number): number => {
  if (typeof value === 'number') {
    // Se já é número, apenas retorna o valor
    return value
  }
  
  // Remove espaços
  let normalized = String(value).trim()
  
  // Se está vazio, retorna 0
  if (!normalized) return 0
  
  // Converte vírgula para ponto (pode haver múltiplas vírgulas, usar replaceAll)
  normalized = normalized.replaceAll(',', '.')
  
  // Remove zeros à esquerda (exceto se for 0.xx ou apenas 0)
  if (normalized.startsWith('0') && normalized.length > 1 && normalized[1] !== '.') {
    normalized = normalized.replace(/^0+/, '')
  }
  
  // Se ficou vazio ou só tem ponto, retorna 0
  if (!normalized || normalized === '.' || normalized === ',') {
    return 0
  }
  
  const parsed = Number.parseFloat(normalized)
  if (Number.isNaN(parsed)) return 0
  
  // Remove zeros à direita desnecessários (mas mantém 2 casas decimais se houver)
  return parseFloat(parsed.toFixed(2))
}

// Função para formatar preço para exibição (usa ponto como separador decimal)
const formatPriceForDisplay = (value: number | string): string => {
  const num = typeof value === 'string' ? normalizePrice(value) : value
  if (num === 0) return '0.00'
  // Formata com 2 casas decimais e usa ponto como separador
  // toFixed já retorna com ponto, mas garantir que não há vírgulas
  return num.toFixed(2).replaceAll(',', '.')
}

// Função para identificar se é manutenção ou avaria baseado na descrição
const getWorkOrderType = (description?: string, diagnosis?: string): 'maintenance' | 'breakdown' => {
  if (!description && !diagnosis) return 'breakdown'
  
  const text = `${description || ''} ${diagnosis || ''}`.toLowerCase()
  
  // Palavras-chave para manutenção
  const maintenanceKeywords = [
    'revisão', 'revisao', 'manutenção', 'manutencao', 'preventiva', 
    'periódica', 'periodica', 'rotina', 'check', 'inspeção', 'inspecao',
    'óleo', 'oleo', 'filtro', 'pneus', 'pneu', 'limpeza', 'calibração', 'calibracao'
  ]
  
  // Palavras-chave para avaria
  const breakdownKeywords = [
    'avaria', 'quebrado', 'partido', 'não funciona', 'nao funciona',
    'defeito', 'problema', 'ruído', 'ruido', 'barulho', 'fumo', 'fumaça', 'fumaca',
    'luz', 'aviso', 'alerta', 'pisca', 'está a', 'esta a', 'parou', 'não arranca', 'nao arranca'
  ]
  
  const maintenanceCount = maintenanceKeywords.filter(keyword => text.includes(keyword)).length
  const breakdownCount = breakdownKeywords.filter(keyword => text.includes(keyword)).length
  
  return maintenanceCount > breakdownCount ? 'maintenance' : 'breakdown'
}

// Componente para exibir histórico de folhas de obra do cliente
function CustomerWorkOrderHistory({ 
  customerCompanyId, 
  workshopCompanyId,
  currentWorkOrderId 
}: { 
  customerCompanyId: number
  workshopCompanyId: number
  currentWorkOrderId: number
}) {
  const navigate = useNavigate()
  const { data: historyWorkOrders, isLoading } = useQuery(
    ['customerWorkOrderHistory', customerCompanyId, workshopCompanyId],
    () => workOrderService.getByCustomer(customerCompanyId, workshopCompanyId, 0, 10),
    {
      enabled: !!customerCompanyId && !!workshopCompanyId,
    }
  )

  if (isLoading) {
    return (
      <div className="px-6 pb-6 border-t border-secondary-100 pt-4">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-secondary-400" />
        </div>
      </div>
    )
  }

  // Filtrar a folha de obra atual e ordenar por data (mais recentes primeiro)
  const filteredHistory = (historyWorkOrders || [])
    .filter(wo => wo.id !== currentWorkOrderId)
    .sort((a, b) => {
      const dateA = new Date(a.createdAt || '').getTime()
      const dateB = new Date(b.createdAt || '').getTime()
      return dateB - dateA
    })

  if (filteredHistory.length === 0) {
    return (
      <div className="px-6 pb-6 border-t border-secondary-100 pt-4">
        <p className="text-sm text-secondary-500">Nenhuma folha de obra anterior encontrada para este cliente.</p>
      </div>
    )
  }

  return (
    <div className="px-6 pb-6 border-t border-secondary-100 pt-4 space-y-3">
      {filteredHistory.map((wo) => (
        <button
          key={wo.id}
          onClick={() => navigate(`/work-orders/${wo.id}`)}
          className="w-full text-left rounded-lg border border-secondary-200 bg-white p-4 hover:border-primary-300 hover:shadow-sm transition-all"
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-semibold text-secondary-900">{wo.workOrderNumber}</span>
                <span className={cn(
                  'px-2 py-0.5 rounded-full text-xs font-semibold',
                  wo.status === WorkOrderStatus.COMPLETED ? 'bg-green-100 text-green-800' :
                  wo.status === WorkOrderStatus.INVOICED ? 'bg-blue-100 text-blue-800' :
                  wo.status === WorkOrderStatus.CANCELLED ? 'bg-red-100 text-red-800' :
                  'bg-yellow-100 text-yellow-800'
                )}>
                  {formatStatus(wo.status)}
                </span>
              </div>
              {wo.description && (
                <p className="text-xs text-secondary-600 mt-1 line-clamp-1">{wo.description}</p>
              )}
              {wo.createdAt && (
                <p className="text-xs text-secondary-400 mt-1">
                  {formatDate(wo.createdAt)}
                </p>
              )}
            </div>
            <FileText className="h-4 w-4 text-secondary-400" />
          </div>
        </button>
      ))}
    </div>
  )
}

export function WorkOrderDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const workOrderId = id ? Number.parseInt(id, 10) : null
  const [isChangingStatus, setIsChangingStatus] = useState(false)
  const [statusChangeNotes, setStatusChangeNotes] = useState('')
  const [wordCount, setWordCount] = useState(0)
  const [selectedNewStatus, setSelectedNewStatus] = useState<WorkOrderStatus | null>(null)
  const [showArrivalConfirmModal, setShowArrivalConfirmModal] = useState(false)
  const [showResolveConfirmModal, setShowResolveConfirmModal] = useState(false)
  const [resolveConfirmMissingFields, setResolveConfirmMissingFields] = useState<string[]>([])
  const [showCompleteConfirmModal, setShowCompleteConfirmModal] = useState(false)
  const [completeRevisionError, setCompleteRevisionError] = useState<string | null>(null)
  const completeRevisionPlanRef = useRef<VehicleRevisionPlanPanelHandle>(null)
  const [isConfirmingArrival, setIsConfirmingArrival] = useState(false)
  const [isCancellingScheduling, setIsCancellingScheduling] = useState(false)
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false)
  const [isOpeningPdf, setIsOpeningPdf] = useState(false)
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null)
  // Estados para gestão manual de serviços e peças (apenas para mecânicos)
  const [isSavingItems, setIsSavingItems] = useState(false)
  // editingServiceItem e editingPieceItem podem ser usados no futuro para edição inline
  // const [editingServiceItem, setEditingServiceItem] = useState<number | null>(null)
  // const [editingPieceItem, setEditingPieceItem] = useState<number | null>(null)
  const [newServiceItem, setNewServiceItem] = useState<Partial<WorkOrderServiceItem>>({ serviceDescription: '', hours: 0, hourlyRate: 1, notes: '' })
  const [newPieceItem, setNewPieceItem] = useState<Partial<WorkOrderPieceItem>>({ pieceDescription: '', quantity: 1, unitPrice: 0, notes: '' })
  const [showAddService, setShowAddService] = useState(false)
  const [showAddPiece, setShowAddPiece] = useState(false)
  const [selectedServiceCostId, setSelectedServiceCostId] = useState<number | null>(null)
  const [selectedPieceCostId, setSelectedPieceCostId] = useState<number | null>(null)
  const [showReceptionistData, setShowReceptionistData] = useState(false)
  const [showMechanicData, setShowMechanicData] = useState(false)
  const [showCustomerHistoryPanel, setShowCustomerHistoryPanel] = useState(false)
  const [selectedMechanicId, setSelectedMechanicId] = useState<number | null>(null)
  const [isSavingMechanicAssignment, setIsSavingMechanicAssignment] = useState(false)
  // Estados para dados da rececionista (editáveis por rececionista, mecânico e administrador)
  const [receptionistDescription, setReceptionistDescription] = useState('')
  const [receptionistDiagnosis, setReceptionistDiagnosis] = useState('')
  const [receptionistObservations, setReceptionistObservations] = useState('')
  const [isSavingReceptionistData, setIsSavingReceptionistData] = useState(false)
  // Estados para dados do mecânico (editáveis apenas por mecânico)
  const [mechanicDescription, setMechanicDescription] = useState('')
  const [mechanicDiagnosis, setMechanicDiagnosis] = useState('')
  const [mechanicObservations, setMechanicObservations] = useState('')
  const [isSavingMechanicData, setIsSavingMechanicData] = useState(false)
  const [showAddBudgetCost, setShowAddBudgetCost] = useState(false)
  const [newBudgetCostDescription, setNewBudgetCostDescription] = useState('')
  const [newBudgetCostAmount, setNewBudgetCostAmount] = useState('')
  const [isAddingBudgetCost, setIsAddingBudgetCost] = useState(false)
  const [applyVat, setApplyVat] = useState(false)
  const [taxPercentage, setTaxPercentage] = useState(23)
  const [isSavingBudget, setIsSavingBudget] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null)
  const [isAssistantOpen, setIsAssistantOpen] = useState(false)
  const [showAssistantHint, setShowAssistantHint] = useState(false)
  const [showInfo, setShowInfo] = useState(true)
  const [showReceptionistSection, setShowReceptionistSection] = useState(true)
  const [showServices, setShowServices] = useState(true)
  const [showAttachments, setShowAttachments] = useState(true)
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false)
  const [attachmentToDelete, setAttachmentToDelete] = useState<WorkOrderAttachment | null>(null)
  const [showPieces, setShowPieces] = useState(true)
  const [pageError, setPageError] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const { aiEnabled, isLoading: entryLevelLoading, storage: planStorage } = useEntryLevel()
  const maxAttachmentMb = planStorage?.maxAttachmentBytes
    ? Math.round(planStorage.maxAttachmentBytes / (1024 * 1024))
    : 25

  // Definir isMechanic, isReceptionist e isAdmin (administrador pode fazer todas as ações)
  const isMechanic = user?.functionId === 2
  const isReceptionist = user?.functionId === 1
  const isAdmin = (user?.functionId ?? 0) >= 3
  const canActAsMechanic = isMechanic || isAdmin
  const canActAsReceptionist = isReceptionist || isAdmin
  const canManageWorkOrderItems = canActAsMechanic || canActAsReceptionist
  const canEditReceptionistData = canActAsReceptionist || canActAsMechanic
  const canEditBudget = canEditReceptionistData

  const showApiFailure = (error: unknown, context: string) => {
    console.error(context, error)
    handleApiError(error)
    setPageError(TECHNICAL_TEAM_ERROR_MESSAGE)
  }

  const showInlineMessage = (message: string) => {
    setPageError(message)
  }

  // Fechar assistente quando o plano não inclui IA
  useEffect(() => {
    if (!aiEnabled) {
      setIsAssistantOpen(false)
      setShowAssistantHint(false)
    }
  }, [aiEnabled])

  // Rascunho local - salvar automaticamente no localStorage
  const DRAFT_KEY = `workOrderDraft_${workOrderId}`
  
  // Carregar rascunho do localStorage quando a página carrega (mecânico ou admin)
  useEffect(() => {
    if (workOrderId && canActAsMechanic) {
      const draft = localStorage.getItem(DRAFT_KEY)
      if (draft) {
        try {
          const parsed = JSON.parse(draft)
          setMechanicDescription(parsed.mechanicDescription || '')
          setMechanicDiagnosis(parsed.mechanicDiagnosis || '')
          setMechanicObservations(parsed.mechanicObservations || '')
          setHasUnsavedChanges(true)
        } catch (e) {
          console.error('Erro ao carregar rascunho:', e)
        }
      }
    }
  }, [workOrderId, canActAsMechanic, DRAFT_KEY])

  // Salvar rascunho no localStorage sempre que houver alterações
  useEffect(() => {
    if (workOrderId && canActAsMechanic && hasUnsavedChanges) {
      const draft = {
        mechanicDescription,
        mechanicDiagnosis,
        mechanicObservations,
        timestamp: new Date().toISOString(),
      }
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draft))
    }
  }, [mechanicDescription, mechanicDiagnosis, mechanicObservations, hasUnsavedChanges, workOrderId, canActAsMechanic, DRAFT_KEY])

  // Verificar se há alterações não guardadas ao tentar sair
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges && canActAsMechanic) {
        e.preventDefault()
        e.returnValue = 'Vai perder as suas últimas atualizações nesta folha de obra!'
        return e.returnValue
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges, canActAsMechanic])

  // Passar mechanicId se for mecânico para registrar acesso
  const mechanicId = canActAsMechanic ? user?.id : undefined
  const { data: workOrder, isLoading, error, refetch: refetchWorkOrder } = useWorkOrder(workOrderId || 0, mechanicId)

  const computedCosts = useMemo(() => {
    const serviceItems = workOrder?.serviceItems ?? []
    const pieceItems = workOrder?.pieceItems ?? []

    const laborCost = serviceItems.reduce((sum, item) => {
      const subtotal = typeof (item as any).subtotal === 'number'
        ? (item as any).subtotal
        : (Number(item.hours) || 0) * (Number(item.hourlyRate) || 0)
      return sum + (Number.isFinite(subtotal) ? subtotal : 0)
    }, 0)

    const partsCost = pieceItems.reduce((sum, item) => {
      const subtotal = typeof (item as any).subtotal === 'number'
        ? (item as any).subtotal
        : (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0)
      return sum + (Number.isFinite(subtotal) ? subtotal : 0)
    }, 0)

    const discount = Number(workOrder?.discountAmount) || 0

    return { laborCost, partsCost, discount }
  }, [
    workOrder?.serviceItems,
    workOrder?.pieceItems,
    workOrder?.discountAmount,
  ])

  const budgetSummary = useMemo(() => {
    const laborCost = computedCosts.laborCost
    const partsCost = computedCosts.partsCost
    const discount = computedCosts.discount
    const budgetCosts = workOrder?.budgetCosts ?? []
    const extraCostsTotal = budgetCosts.length > 0
      ? budgetCosts.reduce((sum, cost) => sum + (Number(cost.amount) || 0), 0)
      : (Number(workOrder?.extraAmount) || 0)
    const taxBase = Math.max(0, laborCost + partsCost - discount)
    const tax = canEditBudget
      ? (applyVat ? (taxBase * taxPercentage) / 100 : 0)
      : (Number(workOrder?.taxAmount) || 0)
    const finalTotal = taxBase + tax + extraCostsTotal

    return { laborCost, partsCost, extraCostsTotal, budgetCosts, discount, tax, finalTotal }
  }, [
    computedCosts,
    applyVat,
    taxPercentage,
    canEditBudget,
    workOrder?.budgetCosts,
    workOrder?.taxAmount,
  ])

  const completeRevisionSuggestions = useMemo(() => {
    const suggestedDate =
      workOrder?.actualCompletionDate?.slice(0, 10) ?? new Date().toISOString().slice(0, 10)
    const suggestedKm = extractKmFromWorkOrderObservations(workOrder?.observations)
    return { suggestedDate, suggestedKm: suggestedKm ?? undefined }
  }, [workOrder?.actualCompletionDate, workOrder?.observations])

  const aiChatWorkOrderContext = useMemo(() => {
    if (!workOrder) return undefined
    return {
      workOrderId: workOrder.id,
      vehicleId: workOrder.vehicleId,
      customerCompanyId: workOrder.customerCompanyId || undefined,
      workshopCompanyId: workOrder.workshopCompanyId,
      vehicleInfo: workOrder.vehicleInfo || undefined,
      customerName: workOrder.customerCompanyName || undefined,
      pieceItems:
        workOrder.pieceItems?.map((item) => ({
          id: item.id,
          pieceDescription: item.pieceDescription,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
        })) || [],
      serviceItems:
        workOrder.serviceItems?.map((item) => ({
          id: item.id,
          serviceDescription: item.serviceDescription,
          hours: item.hours,
          hourlyRate: item.hourlyRate,
        })) || [],
    }
  }, [workOrder])

  // Catálogo de custos da oficina (para adicionar Serviços/Peças do catálogo)
  const { data: workshopCosts = [] } = useQuery<Cost[]>(
    ['workshopCosts', workOrder?.workshopCompanyId],
    () => costService.getByWorkshop(workOrder!.workshopCompanyId),
    { enabled: !!workOrder?.workshopCompanyId && (showAddService || showAddPiece) }
  )

  const serviceCatalogOptions = useMemo(
    () =>
      (workshopCosts as Cost[])
        .filter((c) => c.type === 'S')
        .map((c) => ({
          costId: c.id,
          description: c.description,
          hourlyRate: c.hourlyRate ?? 0,
          label: `${c.description} — ${(c.hourlyRate ?? 0).toFixed(2)} €/h`,
        })),
    [workshopCosts]
  )

  const pieceCatalogOptions = useMemo(
    () =>
      (workshopCosts as Cost[])
        .filter((c) => c.type === 'P')
        .map((c) => ({
          costId: c.id,
          description: c.description,
          unitPrice: c.unitPrice ?? 0,
          label: `${c.description} — ${(c.unitPrice ?? 0).toFixed(2)} €`,
        })),
    [workshopCosts]
  )

  // Buscar todos os usuários ativos da oficina para o dropdown de atribuição
  const { data: workshopUsers = [] } = useQuery<User[]>(
    ['workshopUsers', workOrder?.workshopCompanyId],
    () => userService.getActiveByWorkshop(workOrder!.workshopCompanyId),
    {
      enabled: !!workOrder?.workshopCompanyId,
    }
  )

  const showWorkOrderAssistant =
    canActAsMechanic && !!workOrder && !entryLevelLoading && aiEnabled

  // Dica: aparece 5s após entrar na folha e desaparece ao fim de 5s ou ao clicar
  useEffect(() => {
    if (!showWorkOrderAssistant || !workOrderId) {
      setShowAssistantHint(false)
      return
    }

    setShowAssistantHint(false)
    const showTimer = window.setTimeout(() => setShowAssistantHint(true), 5000)
    return () => window.clearTimeout(showTimer)
  }, [showWorkOrderAssistant, workOrderId])

  useEffect(() => {
    if (isAssistantOpen) setShowAssistantHint(false)
  }, [isAssistantOpen])

  useEffect(() => {
    if (!showAssistantHint) return
    const hideTimer = window.setTimeout(() => setShowAssistantHint(false), 5000)
    return () => window.clearTimeout(hideTimer)
  }, [showAssistantHint])

  // Carregar histórico de alterações de status
  const { data: statusHistory = [] } = useQuery(
    ['workOrderStatusHistory', workOrderId],
    () => workOrderService.getStatusHistory(workOrderId!),
    {
      enabled: !!workOrderId && canActAsMechanic,
    }
  )

  // Anexos (lazy: só carrega quando a secção está visível)
  const { data: attachments = [], refetch: refetchAttachments } = useQuery(
    ['workOrderAttachments', workOrderId],
    () => workOrderAttachmentService.getAttachments(workOrderId!),
    { enabled: !!workOrderId && showAttachments }
  )

  // Função para contar palavras (apenas palavras com 3+ letras)
  const countWords = (text: string): number => {
    if (!text.trim()) return 0
    const words = text.trim().split(/\s+/)
    return words.filter(word => word.length >= 3).length
  }

  // Atualizar contador quando o texto muda
  useEffect(() => {
    setWordCount(countWords(statusChangeNotes))
  }, [statusChangeNotes])

  // Resetar status selecionado quando workOrder muda
  useEffect(() => {
    if (workOrder) {
      setSelectedNewStatus(null)
    }
  }, [workOrder?.id])

  // Garantir que a página sempre comece no topo quando abrir uma nova folha de obra
  useEffect(() => {
    if (workOrderId) {
      // Scroll imediato quando muda o ID da folha de obra
      window.scrollTo({ top: 0, behavior: 'instant' })
    }
  }, [workOrderId])

  // Scroll para o topo quando os dados são carregados pela primeira vez; ou para #attachments
  useEffect(() => {
    if (workOrder && !isLoading) {
      const timer = setTimeout(() => {
        if (window.location.hash === '#attachments') {
          const el = document.getElementById('attachments')
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        } else if (window.scrollY > 50) {
          window.scrollTo({ top: 0, behavior: 'instant' })
        }
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [workOrder?.id])

  // Marcar notificações relacionadas a esta folha de obra como lidas quando entrar na página
  useEffect(() => {
    if (workOrderId && user?.id) {
      notificationService.markAsReadByWorkOrder(workOrderId)
        .then(() => {
          invalidateNotifications(queryClient, user.id)
        })
        .catch((error) => {
          console.error('Erro ao marcar notificações como lidas:', error)
        })
    }
  }, [workOrderId, user?.id, queryClient])

  // Inicializar dados quando workOrder carregar (apenas se não houver rascunho local)
  useEffect(() => {
    if (workOrder) {
      // Dados da rececionista (sempre do workOrder.description, diagnosis, observations)
      setReceptionistDescription(workOrder.description || '')
      setReceptionistDiagnosis(workOrder.diagnosis || '')
      setReceptionistObservations(workOrder.observations || '')
      
      // Dados do mecânico - só inicializar se não houver rascunho local
      if (canActAsMechanic && workOrderId) {
        const draft = localStorage.getItem(DRAFT_KEY)
        if (!draft) {
          setMechanicDescription(workOrder.mechanicDescription || '')
          setMechanicDiagnosis(workOrder.mechanicDiagnosis || '')
          setMechanicObservations(workOrder.mechanicObservations || '')
          setHasUnsavedChanges(false)
        }
      } else {
        setMechanicDescription(workOrder.mechanicDescription || '')
        setMechanicDiagnosis(workOrder.mechanicDiagnosis || '')
        setMechanicObservations(workOrder.mechanicObservations || '')
      }

      // Inicializar selectedMechanicId
      setSelectedMechanicId(workOrder.assignedMechanicId || null)

      const storedTaxPercentage = Number(workOrder.taxPercentage) || 0
      setApplyVat(storedTaxPercentage > 0)
      setTaxPercentage(storedTaxPercentage > 0 ? storedTaxPercentage : 23)
    }
  }, [workOrder, canActAsMechanic, workOrderId, DRAFT_KEY])

  // Função para salvar dados da rececionista (rececionista, mecânico ou administrador)
  const handleSaveReceptionistData = async () => {
    if (!workOrder || !canEditReceptionistData || !user?.id || isSavingReceptionistData) return

    setIsSavingReceptionistData(true)
    try {
      await workOrderService.update(workOrder.id, {
        description: capitalizeFirstLetter(receptionistDescription.trim()),
        diagnosis: capitalizeFirstLetter(receptionistDiagnosis.trim()),
        observations: capitalizeFirstLetter(receptionistObservations.trim()),
        updatedBy: user.id,
      })

      await refetchWorkOrder()
      queryClient.invalidateQueries(['workOrder', workOrder.id])
      setShowReceptionistData(false)
    } catch (error: unknown) {
      showApiFailure(error, 'Erro ao salvar dados da rececionista')
    } finally {
      setIsSavingReceptionistData(false)
    }
  }

  const parseBudgetNumber = (value: string): number => {
    const normalized = value.replace(',', '.').trim()
    const parsed = Number(normalized)
    return Number.isFinite(parsed) ? parsed : 0
  }

  const saveTaxSettings = async (vatEnabled = applyVat, vatPercentage = taxPercentage) => {
    if (!workOrder || !canEditBudget || !user?.id || isSavingBudget) return

    setIsSavingBudget(true)
    try {
      const parsedTaxPercentage = vatEnabled ? parseBudgetNumber(String(vatPercentage)) : 0
      const updated = await workOrderService.update(workOrder.id, {
        taxPercentage: parsedTaxPercentage,
        useManualFinalAmount: false,
        updatedBy: user.id,
      })
      queryClient.setQueryData(['workOrder', workOrder.id, mechanicId], updated)
      void queryClient.invalidateQueries('workOrders')
    } catch (error: unknown) {
      showApiFailure(error, 'Erro ao salvar IVA')
    } finally {
      setIsSavingBudget(false)
    }
  }

  const handleAddBudgetCost = async () => {
    if (!workOrder || !canEditBudget || !user?.id || isAddingBudgetCost) return

    const description = newBudgetCostDescription.trim()
    const amount = parseBudgetNumber(newBudgetCostAmount)
    if (!description) {
      showInlineMessage('Indique uma descrição para o custo.')
      return
    }
    if (newBudgetCostAmount.trim() === '' || !Number.isFinite(amount)) {
      showInlineMessage('Indique um valor válido para o custo.')
      return
    }

    setIsAddingBudgetCost(true)
    try {
      await workOrderService.addBudgetCost(workOrder.id, { description, amount })

      await workOrderService.update(workOrder.id, {
        taxPercentage: applyVat ? parseBudgetNumber(String(taxPercentage)) : 0,
        useManualFinalAmount: false,
        updatedBy: user.id,
      })

      setNewBudgetCostDescription('')
      setNewBudgetCostAmount('')
      setShowAddBudgetCost(false)
      await refetchWorkOrder()
      queryClient.invalidateQueries(['workOrder', workOrder.id])
      void queryClient.invalidateQueries('workOrders')
    } catch (error: unknown) {
      showApiFailure(error, 'Erro ao adicionar custo')
    } finally {
      setIsAddingBudgetCost(false)
    }
  }

  const handleRemoveBudgetCost = async (costId: number) => {
    if (!workOrder || !canEditBudget || isAddingBudgetCost) return

    setIsAddingBudgetCost(true)
    try {
      await workOrderService.removeBudgetCost(workOrder.id, costId)
      await refetchWorkOrder()
      queryClient.invalidateQueries(['workOrder', workOrder.id])
      void queryClient.invalidateQueries('workOrders')
    } catch (error: unknown) {
      showApiFailure(error, 'Erro ao remover custo')
    } finally {
      setIsAddingBudgetCost(false)
    }
  }

  // Função para salvar dados do mecânico (apenas mecânico pode editar)
  const handleSaveMechanicData = async () => {
    if (!workOrder || !canActAsMechanic || !user?.id || isSavingMechanicData) return

    setIsSavingMechanicData(true)
    try {
      await workOrderService.update(workOrder.id, {
        mechanicDescription: capitalizeFirstLetter(mechanicDescription.trim()),
        mechanicDiagnosis: capitalizeFirstLetter(mechanicDiagnosis.trim()),
        mechanicObservations: capitalizeFirstLetter(mechanicObservations.trim()),
        updatedBy: user.id,
      })

      // Limpar rascunho local após salvar com sucesso
      localStorage.removeItem(DRAFT_KEY)
      setHasUnsavedChanges(false)

      await refetchWorkOrder()
      queryClient.invalidateQueries(['workOrder', workOrder.id])
    } catch (error: unknown) {
      showApiFailure(error, 'Erro ao salvar dados do mecânico')
    } finally {
      setIsSavingMechanicData(false)
    }
  }

  // Atualizar estado de alterações não guardadas quando os campos mudam
  useEffect(() => {
    if (workOrder && canActAsMechanic) {
      const hasDescriptionChanged = (mechanicDescription.trim() || '') !== (workOrder.mechanicDescription || '')
      const hasDiagnosisChanged = (mechanicDiagnosis.trim() || '') !== (workOrder.mechanicDiagnosis || '')
      const hasObservationsChanged = (mechanicObservations.trim() || '') !== (workOrder.mechanicObservations || '')
      
      const hasChanges = hasDescriptionChanged || hasDiagnosisChanged || hasObservationsChanged
      setHasUnsavedChanges(hasChanges)
    }
  }, [mechanicDescription, mechanicDiagnosis, mechanicObservations, workOrder, canActAsMechanic])

  // Bloquear navegação quando há alterações não guardadas
  // Nota: useBlocker requer data router, então usamos beforeunload event e interceptação manual
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges && canActAsMechanic) {
        e.preventDefault()
        e.returnValue = 'Vai perder as suas últimas atualizações nesta folha de obra!'
        return e.returnValue
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges, canActAsMechanic])

  // Interceptar navegação quando há alterações não guardadas
  const handleNavigate = (navigationFn: () => void) => {
    if (hasUnsavedChanges && canActAsMechanic) {
      setPendingNavigation(() => navigationFn)
      setShowUnsavedDialog(true)
    } else {
      navigationFn()
    }
  }

  // Funções para o diálogo
  const handleSaveAndExit = async () => {
    await handleSaveMechanicData()
    setShowUnsavedDialog(false)
    if (pendingNavigation) {
      const navFn = pendingNavigation
      setPendingNavigation(null)
      navFn()
    }
    // Note: blocker was removed - using beforeunload event and manual interception instead
  }

  const handleExitWithoutSaving = () => {
    // Limpar rascunho local e sair sem guardar
    localStorage.removeItem(DRAFT_KEY)
    setHasUnsavedChanges(false)
    setShowUnsavedDialog(false)
    if (pendingNavigation) {
      const navFn = pendingNavigation
      setPendingNavigation(null)
      navFn()
    }
    // Note: blocker was removed - using beforeunload event and manual interception instead
  }

  const handleCancelExit = () => {
    setShowUnsavedDialog(false)
    setPendingNavigation(null)
    // Note: blocker was removed - using beforeunload event and manual interception instead
  }

  // Função para salvar itens (serviços e peças) - ENTRADA MANUAL pelo mecânico
  const handleSaveItems = async (serviceItems: WorkOrderServiceItem[], pieceItems: WorkOrderPieceItem[]) => {
    if (!workOrder || !canManageWorkOrderItems) return

    setIsSavingItems(true)
    try {
      const serviceItemsToSave = serviceItems.map(item => ({
        ...(item.costId && { costId: item.costId }),
        serviceDescription: capitalizeFirstLetter(item.serviceDescription.trim()),
        hours: Number(item.hours) || 0,
        hourlyRate: normalizePrice(item.hourlyRate) || 1, // Normaliza e garante mínimo 1
        notes: item.notes ? capitalizeFirstLetter(item.notes.trim()) : '',
      }))

      const pieceItemsToSave = pieceItems.map(item => ({
        ...(item.costId && { costId: item.costId }),
        ...(item.pieceId && item.pieceId > 0 && item.companyPieceId && { pieceId: item.pieceId }),
        pieceDescription: capitalizeFirstLetter(item.pieceDescription.trim()),
        quantity: Number(item.quantity) || 1,
        unitPrice: normalizePrice(item.unitPrice) || 0,
        notes: item.notes ? capitalizeFirstLetter(item.notes.trim()) : '',
        companyPieceId: item.companyPieceId || undefined,
      }))

      await workOrderService.update(workOrder.id, {
        serviceItems: serviceItemsToSave as any,
        pieceItems: pieceItemsToSave as any,
        updatedBy: user?.id,
      })

      // Recarregar dados
      await refetchWorkOrder()
      queryClient.invalidateQueries(['workOrder', workOrder.id])
      
      // Scroll para o topo após salvar
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }, 100)
      
      // Limpar formulários
      setShowAddService(false)
      setShowAddPiece(false)
      setNewServiceItem({ serviceDescription: '', hours: 0, hourlyRate: 1, notes: '' })
      setNewPieceItem({ pieceDescription: '', quantity: 1, unitPrice: 0, notes: '' })
    } catch (error: unknown) {
      showApiFailure(error, 'Erro ao salvar itens')
    } finally {
      setIsSavingItems(false)
    }
  }

  // Adicionar novo serviço (nome livre ou sugestão do catálogo)
  const handleAddService = async () => {
    if (!workOrderId || !newServiceItem.serviceDescription?.trim() || !newServiceItem.hours || newServiceItem.hours <= 0) return

    const normalizedHourlyRate = normalizePrice(newServiceItem.hourlyRate || 1)
    if (normalizedHourlyRate <= 0) {
      showInlineMessage('A taxa horária deve ser maior que zero')
      return
    }

    setIsSavingItems(true)
    try {
      await workOrderService.addServiceItem(workOrderId, {
        ...(selectedServiceCostId != null && { costId: selectedServiceCostId }),
        serviceDescription: capitalizeFirstLetter(newServiceItem.serviceDescription.trim()),
        hours: Number(newServiceItem.hours) || 0,
        hourlyRate: normalizedHourlyRate,
        notes: newServiceItem.notes ? capitalizeFirstLetter(newServiceItem.notes.trim()) : undefined,
      })
      queryClient.invalidateQueries(['workOrder', workOrderId])
      queryClient.invalidateQueries(['workshopCosts', workOrder?.workshopCompanyId])
      await refetchWorkOrder()
      setShowAddService(false)
      setSelectedServiceCostId(null)
      setNewServiceItem({ serviceDescription: '', hours: 0, hourlyRate: 1, notes: '' })
    } catch (error: unknown) {
      showApiFailure(error, 'Erro ao adicionar serviço')
    } finally {
      setIsSavingItems(false)
    }
  }

  // Adicionar nova peça (nome livre ou sugestão do catálogo)
  const handleAddPiece = async () => {
    if (!workOrderId || !newPieceItem.pieceDescription?.trim() || !newPieceItem.quantity || newPieceItem.quantity <= 0) return

    const normalizedUnitPrice = normalizePrice(newPieceItem.unitPrice || 0)
    if (normalizedUnitPrice <= 0) {
      showInlineMessage('O preço unitário deve ser maior que zero')
      return
    }

    setIsSavingItems(true)
    try {
      await workOrderService.addPieceItem(workOrderId, {
        ...(selectedPieceCostId != null && { costId: selectedPieceCostId }),
        pieceDescription: capitalizeFirstLetter(newPieceItem.pieceDescription.trim()),
        quantity: Number(newPieceItem.quantity) || 1,
        unitPrice: normalizedUnitPrice,
        notes: newPieceItem.notes ? capitalizeFirstLetter(newPieceItem.notes.trim()) : undefined,
      })
      queryClient.invalidateQueries(['workOrder', workOrderId])
      queryClient.invalidateQueries(['workshopCosts', workOrder?.workshopCompanyId])
      await refetchWorkOrder()
      setShowAddPiece(false)
      setSelectedPieceCostId(null)
      setNewPieceItem({ pieceDescription: '', quantity: 1, unitPrice: 0, notes: '' })
    } catch (error: unknown) {
      showApiFailure(error, 'Erro ao adicionar peça')
    } finally {
      setIsSavingItems(false)
    }
  }

  // Remover serviço (usa endpoint dedicado quando tem id, senão replace completo)
  const handleRemoveService = async (index: number) => {
    if (!workOrder) return
    const item = workOrder.serviceItems?.[index]
    if (item?.id) {
      setIsSavingItems(true)
      try {
        await workOrderService.removeServiceItem(workOrder.id, item.id)
        await refetchWorkOrder()
        queryClient.invalidateQueries(['workOrder', workOrder.id])
      } catch (error: unknown) {
        showApiFailure(error, 'Erro ao remover serviço')
      } finally {
        setIsSavingItems(false)
      }
    } else {
      const updatedServiceItems = workOrder.serviceItems?.filter((_, i) => i !== index) || []
      handleSaveItems(updatedServiceItems, workOrder.pieceItems || [])
    }
  }

  // Remover peça (usa endpoint dedicado quando tem id, senão replace completo)
  const handleRemovePiece = async (index: number) => {
    if (!workOrder) return
    const item = workOrder.pieceItems?.[index]
    if (item?.id) {
      setIsSavingItems(true)
      try {
        await workOrderService.removePieceItem(workOrder.id, item.id)
        await refetchWorkOrder()
        queryClient.invalidateQueries(['workOrder', workOrder.id])
      } catch (error: unknown) {
        showApiFailure(error, 'Erro ao remover peça')
      } finally {
        setIsSavingItems(false)
      }
    } else {
      const updatedPieceItems = workOrder.pieceItems?.filter((_, i) => i !== index) || []
      handleSaveItems(workOrder.serviceItems || [], updatedPieceItems)
    }
  }

  const performStatusUpdate = async (status: WorkOrderStatus) => {
    if (!workOrderId || !user?.id || !workOrder) return

    setIsChangingStatus(true)
    try {
      const notes = statusChangeNotes.trim() || undefined
      await workOrderService.updateStatus(workOrderId, status, user.id, notes)
      setStatusChangeNotes('')
      setWordCount(0)
      setSelectedNewStatus(null)
      setShowResolveConfirmModal(false)
      setResolveConfirmMissingFields([])
      setShowCompleteConfirmModal(false)
      setCompleteRevisionError(null)
      queryClient.invalidateQueries(['workOrder', workOrderId])
      queryClient.invalidateQueries(['workOrderStatusHistory', workOrderId])
      invalidateNotifications(queryClient, user.id)
      await refetchWorkOrder()
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }, 100)
    } catch (error: unknown) {
      showApiFailure(error, 'Erro ao alterar status da folha de obra')
    } finally {
      setIsChangingStatus(false)
    }
  }

  const requestWorkOrderResolve = async () => {
    if (!workOrderId || !user?.id || !workOrder) return

    if (hasUnsavedChanges && canActAsMechanic) {
      showInlineMessage('Guarde as alterações antes de marcar a folha como Resolvida.')
      return
    }

    try {
      const check = await workOrderService.getCompletionReadiness(workOrderId)
      setResolveConfirmMissingFields(check.missingFields)
      setShowResolveConfirmModal(true)
    } catch (error: unknown) {
      showApiFailure(error, 'Erro ao verificar dados da folha de obra')
    }
  }

  const handleConfirmResolve = async () => {
    await performStatusUpdate(WorkOrderStatus.RESOLVED)
  }

  const requestWorkOrderCompletion = async () => {
    if (!workOrderId || !user?.id || !workOrder || !canCompleteWorkOrder) return

    if (hasUnsavedChanges && canActAsMechanic) {
      showInlineMessage('Guarde as alterações antes de concluir a folha de obra.')
      return
    }

    setCompleteRevisionError(null)
    setShowCompleteConfirmModal(true)
  }

  const handleConfirmComplete = async () => {
    if (!workOrderId || !user?.id || !workOrder) return

    const revisionPanel = completeRevisionPlanRef.current
    if (revisionPanel) {
      const validationError = revisionPanel.getValidationError()
      if (validationError) {
        setCompleteRevisionError(validationError)
        return
      }
    }

    setCompleteRevisionError(null)
    setIsChangingStatus(true)
    try {
      if (revisionPanel && workOrder.vehicleId) {
        const body = revisionPanel.getRevisionPlanBody()
        if (body) {
          await vehicleService.patchRevisionPlan(workOrder.vehicleId, body)
          await queryClient.invalidateQueries(['revision-alerts', user.workshopCompanyId])
          await queryClient.invalidateQueries(['vehicle', workOrder.vehicleId])
        }
      }
      await performStatusUpdate(WorkOrderStatus.COMPLETED)
    } catch (error: unknown) {
      showApiFailure(error, 'Erro ao concluir folha de obra')
      setIsChangingStatus(false)
    }
  }

  const handleStatusChange = async () => {
    if (!workOrderId || !user?.id || !workOrder || !selectedNewStatus) return

    if (selectedNewStatus === WorkOrderStatus.RESOLVED) {
      await requestWorkOrderResolve()
      return
    }

    if (selectedNewStatus === WorkOrderStatus.COMPLETED) {
      await requestWorkOrderCompletion()
      return
    }

    await performStatusUpdate(selectedNewStatus)
  }

  const handleConfirmVehicleArrival = async () => {
    if (!workOrderId || !user?.id) return
    setIsConfirmingArrival(true)
    try {
      await workOrderService.confirmScheduledArrival(workOrderId, user.id)
      setShowArrivalConfirmModal(false)
      queryClient.invalidateQueries(['workOrder', workOrderId])
      queryClient.invalidateQueries(['workOrderStatusHistory', workOrderId])
      queryClient.invalidateQueries('workOrders')
      await refetchWorkOrder()
    } catch (error: unknown) {
      showApiFailure(error, 'Erro ao confirmar chegada do veículo')
    } finally {
      setIsConfirmingArrival(false)
    }
  }

  const handleCancelScheduling = async () => {
    if (!workOrderId || !user?.id) return
    if (!window.confirm('Confirma o cancelamento deste agendamento?')) return
    setIsCancellingScheduling(true)
    try {
      await workOrderService.cancelScheduling(workOrderId, user.id)
      queryClient.invalidateQueries(['workOrder', workOrderId])
      queryClient.invalidateQueries(['workOrderStatusHistory', workOrderId])
      queryClient.invalidateQueries('workOrders')
      await refetchWorkOrder()
    } catch (error: unknown) {
      showApiFailure(error, 'Erro ao cancelar agendamento')
    } finally {
      setIsCancellingScheduling(false)
    }
  }

  const isScheduledWorkOrder = workOrder?.status === WorkOrderStatus.SCHEDULED

  // Status disponíveis para o mecânico alterar (não pode marcar como COMPLETED, apenas RESOLVED)
  const mechanicAvailableStatuses: WorkOrderStatus[] = [
    WorkOrderStatus.IN_PROGRESS,
    WorkOrderStatus.WAITING_PARTS,
    WorkOrderStatus.WAITING_APPROVAL,
    WorkOrderStatus.RESOLVED,
  ]

  const canCompleteWorkOrder =
    !isScheduledWorkOrder
    && !isWorkOrderEditingLocked(workOrder?.status)
    && (isAdmin || isMechanic || isReceptionist)

  // Estados disponíveis: sempre incluir Concluída (exceto cancelada/faturada/agendada)
  const availableStatuses: WorkOrderStatus[] = (() => {
    if (isScheduledWorkOrder || isWorkOrderEditingLocked(workOrder?.status)) {
      return []
    }
    let statuses: WorkOrderStatus[] = []
    if (isAdmin || isReceptionist) {
      statuses = [...WORK_ORDER_WORKFLOW_STATUSES]
    } else if (isMechanic) {
      statuses = [...mechanicAvailableStatuses, WorkOrderStatus.COMPLETED]
    }
    const current = workOrder?.status
    if (current && !statuses.includes(current)) {
      statuses = [current, ...statuses]
    }
    return statuses.filter((s) => s !== WorkOrderStatus.SCHEDULED)
  })()

  const handleMarkAsCompleted = () => {
    void requestWorkOrderCompletion()
  }

  if (!workOrderId) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-secondary-600">ID da folha de obra inválido</p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-secondary-500" />
      </div>
    )
  }

  if (error || !workOrder) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <p className="text-secondary-600">
          {error ? 'Erro ao carregar folha de obra' : 'Folha de obra não encontrada'}
        </p>
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 bg-secondary-200 text-secondary-800 rounded-lg hover:bg-secondary-300"
        >
          Voltar
        </button>
      </div>
    )
  }

  const statusColors: Record<WorkOrderStatus, string> = {
    [WorkOrderStatus.SCHEDULED]: 'bg-amber-100 text-amber-800',
    [WorkOrderStatus.PENDING]: 'bg-slate-100 text-slate-800',
    [WorkOrderStatus.DRAFT]: 'bg-gray-100 text-gray-800',
    [WorkOrderStatus.IN_PROGRESS]: 'bg-blue-100 text-blue-800',
    [WorkOrderStatus.WAITING_PARTS]: 'bg-yellow-100 text-yellow-800',
    [WorkOrderStatus.WAITING_APPROVAL]: 'bg-orange-100 text-orange-800',
    [WorkOrderStatus.RESOLVED]: 'bg-cyan-100 text-cyan-800',
    [WorkOrderStatus.COMPLETED]: 'bg-green-100 text-green-800',
    [WorkOrderStatus.CANCELLED]: 'bg-red-100 text-red-800',
    [WorkOrderStatus.INVOICED]: 'bg-purple-100 text-purple-800',
  }

  const workOrderType = getWorkOrderType(workOrder.description, workOrder.diagnosis)
  const typeLabel = workOrderType === 'maintenance' ? 'Manutenção' : 'Avaria'
  const typeColor = workOrderType === 'maintenance' 
    ? 'bg-blue-100 text-blue-800 border-blue-200' 
    : 'bg-red-100 text-red-800 border-red-200'

  return (
    <div className={cn('space-y-6', showWorkOrderAssistant && 'pb-24 sm:pb-28')}>
      {pageError && (
        <div
          role="alert"
          className="flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
        >
          <span>{pageError}</span>
          <button
            type="button"
            onClick={() => setPageError(null)}
            className="shrink-0 rounded p-1 text-red-700 hover:bg-red-100"
            aria-label="Fechar mensagem"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      {showResolveConfirmModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="resolve-confirm-title"
        >
          <div
            className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="resolve-confirm-title" className="text-lg font-semibold text-secondary-900 mb-2">
              Quer marcar a folha como Resolvida?
            </h3>
            {resolveConfirmMissingFields.length > 0 ? (
              <>
                <p className="text-sm text-secondary-700 mb-3">
                  Ainda não tem estes dados preenchidos:
                </p>
                <ul className="mb-4 list-disc space-y-1 pl-5 text-sm text-secondary-800">
                  {resolveConfirmMissingFields.map((field) => (
                    <li key={field}>{field}</li>
                  ))}
                </ul>
                <p className="mb-4 text-xs text-secondary-600">
                  Pode confirmar mesmo assim; a receção poderá completar os dados antes de concluir a
                  folha.
                </p>
              </>
            ) : (
              <p className="mb-4 text-sm text-secondary-700">
                Todos os dados obrigatórios estão preenchidos.
              </p>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowResolveConfirmModal(false)
                  setResolveConfirmMissingFields([])
                  setSelectedNewStatus(null)
                }}
                disabled={isChangingStatus}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
              >
                Negar
              </button>
              <button
                type="button"
                data-testid="confirm-resolve-work-order"
                onClick={() => void handleConfirmResolve()}
                disabled={isChangingStatus}
                className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 rounded-lg disabled:opacity-50"
              >
                {isChangingStatus ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCompleteConfirmModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="complete-confirm-title"
        >
          <div
            className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="complete-confirm-title" className="text-lg font-semibold text-secondary-900 mb-2">
              Quer concluir a folha de obra?
            </h3>
            <p className="mb-4 text-sm text-secondary-700">
              Antes de concluir, pode atualizar o plano de revisão do veículo para os alertas em
              Revisões ficarem corretos.
            </p>

            {workOrder?.vehicleId && user?.workshopCompanyId ? (
              <VehicleRevisionPlanPanel
                ref={completeRevisionPlanRef}
                embedded
                vehicleId={workOrder.vehicleId}
                workshopCompanyId={user.workshopCompanyId}
                suggestedLastRevisionDate={completeRevisionSuggestions.suggestedDate}
                suggestedLastRevisionKm={completeRevisionSuggestions.suggestedKm}
              />
            ) : null}

            {completeRevisionError ? (
              <p className="mt-3 text-sm text-red-700">{completeRevisionError}</p>
            ) : null}

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowCompleteConfirmModal(false)
                  setCompleteRevisionError(null)
                  setSelectedNewStatus(null)
                }}
                disabled={isChangingStatus}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
              >
                Negar
              </button>
              <button
                type="button"
                data-testid="confirm-complete-work-order"
                onClick={() => void handleConfirmComplete()}
                disabled={isChangingStatus}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50"
              >
                {isChangingStatus ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showArrivalConfirmModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="arrival-confirm-title"
        >
          <div
            className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="arrival-confirm-title" className="text-lg font-semibold text-secondary-900 mb-4">
              Confirma a chegada do veículo?
            </h3>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowArrivalConfirmModal(false)}
                disabled={isConfirmingArrival}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmVehicleArrival}
                disabled={isConfirmingArrival}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-lg disabled:opacity-50"
              >
                {isConfirmingArrival ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sim'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Diálogo de confirmação para alterações não guardadas */}
      {showUnsavedDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="h-6 w-6 text-yellow-600" />
              <h3 className="text-lg font-semibold text-secondary-900">
                Alterações não guardadas
              </h3>
            </div>
            <p className="text-sm text-secondary-700 mb-6">
              Vai perder as suas últimas atualizações nesta folha de obra!
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleSaveAndExit}
                disabled={isSavingMechanicData}
                className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="h-4 w-4" />
                Guardar e sair
              </button>
              <button
                onClick={handleExitWithoutSaving}
                className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-red-700"
              >
                <X className="h-4 w-4" />
                Sair sem guardar
              </button>
              <button
                onClick={handleCancelExit}
                className="flex items-center gap-2 rounded-lg border border-gray-300 bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-200"
              >
                <X className="h-4 w-4" />
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmação para eliminar anexo */}
      {attachmentToDelete && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-attachment-title"
          tabIndex={-1}
          onClick={() => setAttachmentToDelete(null)}
          onKeyDown={(e) => e.key === 'Escape' && setAttachmentToDelete(null)}
          ref={(el) => el?.focus()}
        >
          <div
            className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="delete-attachment-title" className="text-lg font-semibold text-secondary-900 mb-3">
              Eliminar anexo
            </h3>
            <p className="text-sm text-secondary-700 mb-6">
              Tem a certeza que quer eliminar o arquivo <strong>{attachmentToDelete.originalFilename}</strong>?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setAttachmentToDelete(null)}
                className="flex items-center gap-2 rounded-lg border border-secondary-300 bg-secondary-100 px-4 py-2 text-sm font-semibold text-secondary-700 transition-all hover:bg-secondary-200"
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  if (!workOrderId || !attachmentToDelete) return
                  try {
                    await workOrderAttachmentService.deleteAttachment(workOrderId, attachmentToDelete.id)
                    refetchAttachments()
                    queryClient.invalidateQueries(['workOrderAttachments', workOrderId])
                    setAttachmentToDelete(null)
                  } catch (e: unknown) {
                    showApiFailure(e, 'Erro ao remover anexo')
                  }
                }}
                className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-red-700"
              >
                <Trash2 className="h-4 w-4" />
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => handleNavigate(() => navigate(-1))}
          className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-secondary-700" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-bold text-secondary-900">{workOrder.workOrderNumber}</h1>
            <span className={cn('px-3 py-1 rounded-full text-xs font-semibold border', typeColor)}>
              {typeLabel}
            </span>
            <span
              className={cn(
                'px-3 py-1 rounded-full text-xs font-semibold',
                statusColors[workOrder.status]
              )}
            >
              {formatStatus(workOrder.status)}
            </span>
          </div>
        </div>
        {/* Work order PDF: pré-visualização em modal + download */}
        <button
          onClick={async () => {
            if (!workOrder?.id || !user?.id) return
            setIsOpeningPdf(true)
            try {
              const url = await workOrderService.fetchPdfBlobUrl(workOrder.id, user.id)
              setPdfPreviewUrl(url)
            } catch (error: unknown) {
              showApiFailure(error, 'Erro ao abrir o PDF')
            } finally {
              setIsOpeningPdf(false)
            }
          }}
          disabled={isOpeningPdf || isDownloadingPdf || !workOrder?.id || !user?.id}
          className="ml-4 flex items-center gap-2 rounded-lg border border-primary-500 bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-primary-600 disabled:bg-secondary-300 disabled:border-secondary-300 disabled:cursor-not-allowed"
          title="Pré-visualizar PDF da folha de obra"
        >
          {isOpeningPdf ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Eye className="h-4 w-4" />
          )}
          {isOpeningPdf ? 'A carregar...' : 'Ver PDF'}
        </button>
        <button
          onClick={async () => {
            if (!workOrder?.id || !user?.id) return
            setIsDownloadingPdf(true)
            try {
              await workOrderService.downloadPdf(workOrder.id, user.id)
            } catch (error: unknown) {
              showApiFailure(error, 'Erro ao fazer download do PDF')
            } finally {
              setIsDownloadingPdf(false)
            }
          }}
          disabled={isDownloadingPdf || isOpeningPdf || !workOrder?.id || !user?.id}
          className="ml-2 flex items-center gap-2 rounded-lg border border-secondary-300 bg-white px-4 py-2 text-sm font-semibold text-secondary-700 transition-all hover:bg-secondary-50 disabled:bg-secondary-100 disabled:cursor-not-allowed"
          title="Descarregar PDF da folha de obra"
        >
          {isDownloadingPdf ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          {isDownloadingPdf ? 'A gerar...' : 'Descarregar'}
        </button>
      </div>

      {/* Layout Completo - Mesmo layout para Rececionista e Mecânico */}
      <>
        <div className="space-y-6">
          <div className="space-y-6">
          {/* Informações Gerais - colapsável em mobile */}
          <CollapsibleSection
            title="Informações Gerais"
            isOpen={showInfo}
            onToggle={() => setShowInfo(!showInfo)}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-secondary-400 mb-1">
                  Cliente
                </p>
                {workOrder.customerCompanyId && workOrder.customerCompanyName ? (
                  <button
                    onClick={() => setShowCustomerHistoryPanel(true)}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer hover:underline"
                    title="Clique para ver o histórico do cliente"
                  >
                    {workOrder.customerCompanyName}
                  </button>
                ) : (
                <p className="text-sm text-secondary-700">
                  {workOrder.customerCompanyName || 'Não definido'}
                </p>
                )}
                {(workOrder.customerEmail || workOrder.customerPhone) && (
                  <div className="mt-2 space-y-1">
                    {workOrder.customerEmail && (
                      <a
                        href={`mailto:${workOrder.customerEmail}`}
                        className="flex items-center gap-1.5 text-xs text-secondary-600 hover:text-primary-600 transition-colors"
                      >
                        <Mail className="h-3.5 w-3.5 shrink-0" />
                        <span>{workOrder.customerEmail}</span>
                      </a>
                    )}
                    {workOrder.customerPhone && (
                      <a
                        href={`tel:${workOrder.customerPhone}`}
                        className="flex items-center gap-1.5 text-xs text-secondary-600 hover:text-primary-600 transition-colors"
                      >
                        <Phone className="h-3.5 w-3.5 shrink-0" />
                        <span>{workOrder.customerPhone}</span>
                      </a>
                    )}
                  </div>
                )}
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-secondary-400 mb-1">
                  Veículo
                </p>
                <p className="text-sm text-secondary-700">
                  {workOrder.vehicleInfo || 'Não definido'}
                </p>
                {workOrder.vehicleRegistrationPlate && (
                  <p className="text-xs text-secondary-500 mt-1">
                    Matrícula: {workOrder.vehicleRegistrationPlate}
                  </p>
                )}
                {(workOrder.vehicleFuelType || workOrder.vehicleColor) && (
                  <div className="mt-1 space-y-0.5">
                    {workOrder.vehicleFuelType && (
                      <p className="text-xs text-secondary-500">
                        Combustível: {workOrder.vehicleFuelType}
                      </p>
                    )}
                    {workOrder.vehicleColor && (
                      <p className="text-xs text-secondary-500">
                        Cor: {workOrder.vehicleColor}
                      </p>
                    )}
                  </div>
                )}
              </div>
              {/* Mecânico Atribuído - Editável por todos */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-secondary-400 mb-1">
                  Mecânico Atribuído
                </p>
                <Select
                  value={selectedMechanicId || ''}
                  onChange={async (e) => {
                    const newMechanicId = e.target.value ? Number(e.target.value) : null
                    setSelectedMechanicId(newMechanicId)
                    
                    // Salvar imediatamente
                    if (!workOrderId || isSavingMechanicAssignment || !user?.id) return
                    setIsSavingMechanicAssignment(true)
                    try {
                      await workOrderService.update(workOrderId, {
                        assignedMechanicId: newMechanicId || undefined,
                        updatedBy: user.id,
                      })
                      await refetchWorkOrder()
                      queryClient.invalidateQueries(['workOrder', workOrderId])
                      invalidateNotifications(queryClient, user.id)
                    } catch (error: unknown) {
                      showApiFailure(error, 'Erro ao atualizar mecânico atribuído')
                      // Reverter para o valor anterior
                      setSelectedMechanicId(workOrder.assignedMechanicId || null)
                    } finally {
                      setIsSavingMechanicAssignment(false)
                    }
                  }}
                  disabled={isSavingMechanicAssignment}
                >
                  <option value="">Nenhum atribuído</option>
                  {workshopUsers.length === 0 ? (
                    <option value="" disabled>Nenhum utilizador disponível</option>
                  ) : (
                    workshopUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))
                  )}
                </Select>
              </div>
            </div>
          </CollapsibleSection>

          {/* Anexar ficheiros - imagens e vídeos (lazy loading) */}
          <div id="attachments">
          <CollapsibleSection
            title="Anexar ficheiros"
            isOpen={showAttachments}
            onToggle={() => setShowAttachments(!showAttachments)}
          >
            <div className="space-y-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/quicktime"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0]
                    if (!file || !workOrderId || !user?.id) return
                    setIsUploadingAttachment(true)
                    try {
                      await workOrderAttachmentService.uploadAttachment(workOrderId, file, user.id)
                      refetchAttachments()
                      queryClient.invalidateQueries(['workOrderAttachments', workOrderId])
                      queryClient.invalidateQueries(['entry-level-me'])
                    } catch (err: unknown) {
                      showApiFailure(err, 'Erro ao anexar ficheiro')
                    } finally {
                      setIsUploadingAttachment(false)
                      e.target.value = ''
                    }
                  }}
                  disabled={isUploadingAttachment}
                />
                <span className="flex items-center gap-2 rounded-lg border border-primary-500 bg-primary-50 px-4 py-2 text-sm font-semibold text-primary-700 hover:bg-primary-100 transition-colors">
                  {isUploadingAttachment ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Paperclip className="h-4 w-4" />
                  )}
                  {isUploadingAttachment ? 'A enviar...' : 'Anexar imagem ou vídeo'}
                </span>
                <span className="text-xs text-secondary-500">
                  (jpg, png, gif, webp, mp4, webm, mov — até {maxAttachmentMb}MB por ficheiro)
                </span>
              </label>
              {planStorage?.quotaLabel && planStorage?.usedLabel && (
                <p className="text-xs text-secondary-600">
                  Armazenamento da oficina: {planStorage.usedLabel} de {planStorage.quotaLabel} utilizados
                  {planStorage.usedPercent != null && planStorage.usedPercent >= 85
                    ? ' — perto do limite do plano'
                    : ''}
                </p>
              )}

              {attachments.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {attachments.map((att: WorkOrderAttachment) => {
                    const filePath = workOrderAttachmentService.getAttachmentFilePath(workOrderId!, att.id)
                    const isImage = att.fileType === 'IMAGE'
                    const isVideo = att.fileType === 'VIDEO'
                    return (
                      <div key={att.id} className="relative group rounded-lg overflow-hidden border border-secondary-200 bg-secondary-50 aspect-square">
                        {isImage && (
                          <button
                            type="button"
                            onClick={() => workOrderAttachmentService.openInNewTab(workOrderId!, att.id)}
                            className="block w-full h-full cursor-pointer"
                          >
                            <AttachmentMedia
                              src={filePath}
                              alt={att.originalFilename}
                              type="image"
                              className="w-full h-full object-cover"
                            />
                          </button>
                        )}
                        {isVideo && (
                          <button
                            type="button"
                            onClick={() => workOrderAttachmentService.openInNewTab(workOrderId!, att.id)}
                            className="block w-full h-full relative cursor-pointer"
                          >
                            <AttachmentMedia
                              src={filePath}
                              type="video"
                              className="w-full h-full object-cover"
                            />
                            <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded bg-black/70 text-white text-xs font-medium">
                              Vídeo
                            </span>
                          </button>
                        )}
                        {!isImage && !isVideo && (
                          <button
                            type="button"
                            onClick={() => workOrderAttachmentService.openInNewTab(workOrderId!, att.id)}
                            className="flex flex-col items-center justify-center h-full p-2 w-full"
                          >
                            <FileText className="h-8 w-8 text-secondary-400" />
                            <span className="text-xs text-secondary-600 truncate w-full text-center mt-1">{att.originalFilename}</span>
                          </button>
                        )}
                        <button
                          onClick={() => setAttachmentToDelete(att)}
                          className="absolute top-2 right-2 p-1.5 rounded-full bg-red-500/90 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                          title="Remover anexo"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )
                  })}
                </div>
              )}
              {attachments.length === 0 && (
                <p className="text-sm text-secondary-500 italic">Nenhum anexo. Clique acima para adicionar imagens ou vídeos.</p>
              )}
            </div>
          </CollapsibleSection>
          </div>

          {/* Dados da Rececionista - Colapsável e editável por rececionista, mecânico e administrador */}
          <CollapsibleSection
            title="Dados da Rececionista"
            isOpen={showReceptionistSection}
            onToggle={() => setShowReceptionistSection(!showReceptionistSection)}
            headerExtra={
              canEditReceptionistData && !showReceptionistData ? (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowReceptionistData(true); }}
                  className="flex items-center gap-2 rounded-lg border border-primary-500 bg-primary-500 px-3 py-1.5 text-xs font-semibold text-white transition-all hover:bg-primary-600 min-h-[44px]"
                >
                  <Edit2 className="h-3 w-3" />
                  Editar
                </button>
              ) : null
            }
          >
            {(showReceptionistData || canEditReceptionistData || (!canEditReceptionistData && showReceptionistSection)) && (
              <div className="space-y-4">
                {canEditReceptionistData && showReceptionistData ? (
                  // Modo de edição
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="receptionist-description" className="text-sm font-semibold text-secondary-700 mb-2 block">
                        {workOrder.description?.startsWith('Manutenção:') || workOrder.description?.startsWith('Manutencao:') ? 'Tipo de Serviço' : 'Descrição do Problema'}
                      </label>
                      <textarea
                        id="receptionist-description"
                        value={receptionistDescription}
                        onChange={(e) => {
                          const value = e.target.value
                          const capitalized = value.length > 0 
                            ? value.charAt(0).toUpperCase() + value.slice(1)
                            : value
                          setReceptionistDescription(capitalized)
                        }}
                        className="w-full rounded-lg border border-secondary-200 bg-white px-3 py-2 text-sm text-secondary-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100 min-h-[80px]"
                        placeholder="Descrição do problema ou tipo de serviço..."
                      />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-secondary-700 mb-2 block">
                        Notas do cliente
                      </label>
                      <textarea
                        value={receptionistDiagnosis}
                        onChange={(e) => {
                          const value = e.target.value
                          const capitalized = value.length > 0 
                            ? value.charAt(0).toUpperCase() + value.slice(1)
                            : value
                          setReceptionistDiagnosis(capitalized)
                        }}
                        className="w-full rounded-lg border border-secondary-200 bg-white px-3 py-2 text-sm text-secondary-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100 min-h-[80px]"
                        placeholder="Notas do cliente..."
                      />
                    </div>
                    <div>
                      <label htmlFor="receptionist-observations" className="text-sm font-semibold text-secondary-700 mb-2 block">
                        Observações
                      </label>
                      <textarea
                        id="receptionist-observations"
                        value={receptionistObservations}
                        onChange={(e) => {
                          const value = e.target.value
                          const capitalized = value.length > 0 
                            ? value.charAt(0).toUpperCase() + value.slice(1)
                            : value
                          setReceptionistObservations(capitalized)
                        }}
                        className="w-full rounded-lg border border-secondary-200 bg-white px-3 py-2 text-sm text-secondary-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100 min-h-[120px]"
                        placeholder="Observações adicionais..."
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleSaveReceptionistData}
                        disabled={isSavingReceptionistData}
                        className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Check className="h-4 w-4" />
                        Salvar
                      </button>
                      <button
                        onClick={() => {
                          setShowReceptionistData(false)
                          // Restaurar valores originais
                          setReceptionistDescription(workOrder.description || '')
                          setReceptionistDiagnosis(workOrder.diagnosis || '')
                          setReceptionistObservations(workOrder.observations || '')
                        }}
                        className="flex items-center gap-2 rounded-lg border border-secondary-300 bg-white px-4 py-2 text-sm font-semibold text-secondary-700 transition-all hover:bg-secondary-50"
                      >
                        <X className="h-4 w-4" />
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  // Modo de visualização (para mecânico ou rececionista quando não está editando)
                  <>
                    {workOrder.description && (
                      <div>
                        <h3 className="text-sm font-semibold text-secondary-700 mb-2">
                          {workOrder.description.toLowerCase().startsWith('manutenção:') || workOrder.description.toLowerCase().startsWith('manutencao:') 
                            ? 'Tipo de Serviço' 
                            : 'Descrição do Problema'}
                        </h3>
                        <p className="text-sm text-secondary-700 whitespace-pre-wrap">
                          {workOrder.description}
                        </p>
                      </div>
                    )}
                    {workOrder.diagnosis && (
                      <div>
                        <h3 className="text-sm font-semibold text-secondary-700 mb-2">Notas do cliente</h3>
                        <p className="text-sm text-secondary-700 whitespace-pre-wrap">
                          {workOrder.diagnosis}
                        </p>
                      </div>
                    )}
                    {workOrder.observations && (
                      <div>
                        <h3 className="text-sm font-semibold text-secondary-700 mb-2">Observações</h3>
                        <FormattedObservations observations={workOrder.observations} />
                      </div>
                    )}
                    {!workOrder.description && !workOrder.diagnosis && !workOrder.observations && (
                      <p className="text-sm text-secondary-500 italic">Nenhum dado registado pela rececionista</p>
                    )}
                  </>
                )}
              </div>
            )}
          </CollapsibleSection>

          {/* Dados do Mecânico - Colapsável com chevron rotativo */}
          <CollapsibleSection
            title="Dados do Mecânico"
            isOpen={showMechanicData}
            onToggle={() => setShowMechanicData(!showMechanicData)}
            headerExtra={
              hasUnsavedChanges && canActAsMechanic ? (
                <span className="px-2 py-1 text-xs font-semibold text-yellow-700 bg-yellow-100 rounded-full">
                  Alterações não guardadas
                </span>
              ) : undefined
            }
          >
            <div className="space-y-4">
                <div className="space-y-4">
                  {/* Descrição - Sempre editável para mecânicos */}
                  <div>
                    <label className="text-sm font-semibold text-secondary-700 mb-2 block">Descrição</label>
                    {canActAsMechanic ? (
                      <textarea
                        value={mechanicDescription}
                        onChange={(e) => {
                          const value = e.target.value
                          const capitalized = capitalizeFirstLetter(value)
                          setMechanicDescription(capitalized)
                        }}
                        rows={3}
                        className="w-full rounded-lg border border-secondary-200 bg-white px-3 py-2 text-sm text-secondary-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                        placeholder="Descrição do trabalho..."
                      />
                    ) : (
                      <p className="text-sm text-secondary-700 whitespace-pre-wrap">
                        {workOrder.mechanicDescription || 'N/A'}
                      </p>
                    )}
                  </div>

                  {/* Diagnóstico - justificação da manutenção ou avaria */}
                  <div>
                    <label className="text-sm font-semibold text-secondary-700 mb-2 block">Diagnóstico</label>
                    {canActAsMechanic ? (
                      <textarea
                        value={mechanicDiagnosis}
                        onChange={(e) => {
                          const value = e.target.value
                          const capitalized = capitalizeFirstLetter(value)
                          setMechanicDiagnosis(capitalized)
                        }}
                        rows={3}
                        className="w-full rounded-lg border border-secondary-200 bg-white px-3 py-2 text-sm text-secondary-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                        placeholder="Justificação da manutenção ou avaria..."
                      />
                    ) : (
                      <p className="text-sm text-secondary-700 whitespace-pre-wrap">
                        {workOrder.mechanicDiagnosis || 'N/A'}
                      </p>
                    )}
                  </div>

                  {/* Observações - Sempre editável para mecânicos */}
                  <div>
                    <label htmlFor="mechanic-observations" className="text-sm font-semibold text-secondary-700 mb-2 block">Observações</label>
                    {canActAsMechanic ? (
                      <div id="mechanic-observations">
                        <MechanicObservationsSections
                          value={mechanicObservations}
                          onChange={(value) => {
                            setMechanicObservations(value)
                          }}
                          disabled={isSavingMechanicData}
                        />
                      </div>
                    ) : (
                      <FormattedObservations observations={workOrder.mechanicObservations || ''} />
                    )}
                  </div>
                </div>
                {canActAsMechanic && (
                  <div className="flex items-center justify-end gap-2 pt-4 border-t border-secondary-100 mt-4">
                    <button
                      onClick={handleSaveMechanicData}
                      disabled={isSavingMechanicData || !hasUnsavedChanges}
                      className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Save className="h-4 w-4" />
                      {isSavingMechanicData ? 'A guardar...' : 'Guardar'}
                    </button>
                  </div>
                )}
            </div>
          </CollapsibleSection>

          {/* Serviços e Peças - colapsáveis em mobile */}
          {workOrder && (
            <div className="space-y-6">
              {/* Serviços */}
              <CollapsibleSection
                title="Serviços"
                subtitle={`${workOrder.serviceItems?.length || 0} serviço(s) adicionado(s)`}
                isOpen={showServices}
                onToggle={() => setShowServices(!showServices)}
                className="bg-gradient-to-br from-white to-secondary-50/30 shadow-md"
                headerExtra={
                  canManageWorkOrderItems && !showAddService ? (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setShowAddService(true); }}
                      disabled={isSavingItems || isWorkOrderEditingLocked(workOrder.status)}
                      className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Plus className="h-4 w-4 shrink-0" />
                      Adicionar
                    </button>
                  ) : undefined
                }
              >
                {/* Lista de Serviços */}
                {workOrder.serviceItems && workOrder.serviceItems.length > 0 && (
                  <div className="space-y-3 mb-4">
                    {workOrder.serviceItems.map((item, index) => (
                      <div key={item.id || index} className="flex items-start justify-between p-4 rounded-lg bg-white border border-secondary-200">
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-secondary-900">{item.serviceDescription}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-secondary-600">
                            <span>{item.hours}h × {formatPriceForDisplay(item.hourlyRate)}€/h</span>
                            <span className="font-semibold text-secondary-900">= {formatPriceForDisplay(item.subtotal || item.hours * item.hourlyRate)}€</span>
                          </div>
                          {item.notes && (
                            <p className="text-xs text-secondary-500 mt-1">{item.notes}</p>
                          )}
                        </div>
                        {canManageWorkOrderItems && (
                        <button
                          onClick={() => handleRemoveService(index)}
                          disabled={isSavingItems || isWorkOrderEditingLocked(workOrder.status)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Formulário para Adicionar Serviço */}
                {canManageWorkOrderItems && showAddService && (
                  <div className="p-4 rounded-lg bg-blue-50/50 border border-blue-200 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2 sm:col-span-1">
                        <CatalogAutocompleteInput
                          label="Nome do serviço"
                          placeholder="Escreva ou escolha do catálogo..."
                          value={newServiceItem.serviceDescription || ''}
                          onValueChange={(text) => {
                            setSelectedServiceCostId(null)
                            setNewServiceItem({ ...newServiceItem, serviceDescription: text })
                          }}
                          onSelectOption={(opt) => {
                            setSelectedServiceCostId(opt.costId)
                            setNewServiceItem({
                              ...newServiceItem,
                              serviceDescription: opt.description,
                              hourlyRate: opt.hourlyRate ?? newServiceItem.hourlyRate ?? 1,
                            })
                          }}
                          options={serviceCatalogOptions}
                          disabled={isSavingItems}
                          inputClassName="border-blue-200 focus:border-blue-500 focus:ring-blue-100"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-secondary-700 mb-1">Horas</label>
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          value={newServiceItem.hours || 0}
                          onChange={(e) => setNewServiceItem({ ...newServiceItem, hours: Number.parseFloat(e.target.value) || 0 })}
                          className="w-full rounded-lg border border-secondary-200 bg-white px-3 py-2 text-sm text-secondary-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-secondary-700 mb-1">Taxa Horária (€)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={newServiceItem.hourlyRate || 1}
                          onChange={(e) => {
                            setSelectedServiceCostId(null)
                            setNewServiceItem({ ...newServiceItem, hourlyRate: Number.parseFloat(e.target.value) || 1 })
                          }}
                          className="w-full rounded-lg border border-secondary-200 bg-white px-3 py-2 text-sm text-secondary-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-secondary-700 mb-1">Notas (opcional)</label>
                        <input
                          type="text"
                          value={newServiceItem.notes || ''}
                          onChange={(e) => setNewServiceItem({ ...newServiceItem, notes: e.target.value })}
                          placeholder="Notas adicionais"
                          className="w-full rounded-lg border border-secondary-200 bg-white px-3 py-2 text-sm text-secondary-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleAddService}
                        disabled={isSavingItems || !newServiceItem.serviceDescription?.trim() || !newServiceItem.hours || newServiceItem.hours <= 0}
                        className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSavingItems ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                        Adicionar
                      </button>
                      <button
                        onClick={() => {
                          setShowAddService(false)
                          setSelectedServiceCostId(null)
                          setNewServiceItem({ serviceDescription: '', hours: 0, hourlyRate: 1, notes: '' })
                        }}
                        className="flex items-center gap-2 rounded-lg bg-secondary-200 px-4 py-2 text-sm font-semibold text-secondary-700 transition-all hover:bg-secondary-300"
                      >
                        <X className="h-4 w-4" />
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}
              </CollapsibleSection>

              {/* Peças */}
              <CollapsibleSection
                title="Peças"
                subtitle={`${workOrder.pieceItems?.length || 0} peça(s) adicionada(s)`}
                isOpen={showPieces}
                onToggle={() => setShowPieces(!showPieces)}
                className="bg-gradient-to-br from-white to-secondary-50/30 shadow-md"
                headerExtra={
                  canManageWorkOrderItems && !showAddPiece ? (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setShowAddPiece(true); }}
                      disabled={isSavingItems || isWorkOrderEditingLocked(workOrder.status)}
                      className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Plus className="h-4 w-4 shrink-0" />
                      Adicionar
                    </button>
                  ) : undefined
                }
              >
                {/* Lista de Peças */}
                {workOrder.pieceItems && workOrder.pieceItems.length > 0 && (
                  <div className="space-y-3 mb-4">
                    {workOrder.pieceItems.map((item, index) => (
                      <div key={item.id || index} className="flex items-start justify-between p-4 rounded-lg bg-white border border-secondary-200">
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-secondary-900">{item.pieceDescription}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-secondary-600">
                            <span>{item.quantity} × {formatPriceForDisplay(item.unitPrice)}€</span>
                            <span className="font-semibold text-secondary-900">= {formatPriceForDisplay(item.subtotal || item.quantity * item.unitPrice)}€</span>
                          </div>
                          {item.notes && (
                            <p className="text-xs text-secondary-500 mt-1">{item.notes}</p>
                          )}
                        </div>
                        {canManageWorkOrderItems && (
                        <button
                          onClick={() => handleRemovePiece(index)}
                          disabled={isSavingItems || isWorkOrderEditingLocked(workOrder.status)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Formulário para Adicionar Peça */}
                {canManageWorkOrderItems && showAddPiece && (
                  <div className="p-4 rounded-lg bg-green-50/50 border border-green-200 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2 sm:col-span-1">
                        <CatalogAutocompleteInput
                          label="Nome da peça"
                          placeholder="Escreva ou escolha do catálogo..."
                          value={newPieceItem.pieceDescription || ''}
                          onValueChange={(text) => {
                            setSelectedPieceCostId(null)
                            setNewPieceItem({ ...newPieceItem, pieceDescription: text })
                          }}
                          onSelectOption={(opt) => {
                            setSelectedPieceCostId(opt.costId)
                            setNewPieceItem({
                              ...newPieceItem,
                              pieceDescription: opt.description,
                              unitPrice: opt.unitPrice ?? newPieceItem.unitPrice ?? 0,
                            })
                          }}
                          options={pieceCatalogOptions}
                          disabled={isSavingItems}
                          inputClassName="border-green-200 focus:border-green-500 focus:ring-green-100"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-secondary-700 mb-1">Quantidade</label>
                        <input
                          type="number"
                          min="1"
                          value={newPieceItem.quantity || 1}
                          onChange={(e) => setNewPieceItem({ ...newPieceItem, quantity: Number.parseInt(e.target.value) || 1 })}
                          className="w-full rounded-lg border border-secondary-200 bg-white px-3 py-2 text-sm text-secondary-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-100"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-secondary-700 mb-1">Preço Unitário (€)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={newPieceItem.unitPrice || 0}
                          onChange={(e) => {
                            setSelectedPieceCostId(null)
                            setNewPieceItem({ ...newPieceItem, unitPrice: Number.parseFloat(e.target.value) || 0 })
                          }}
                          className="w-full rounded-lg border border-secondary-200 bg-white px-3 py-2 text-sm text-secondary-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-100"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-secondary-700 mb-1">Notas (opcional)</label>
                        <input
                          type="text"
                          value={newPieceItem.notes || ''}
                          onChange={(e) => setNewPieceItem({ ...newPieceItem, notes: e.target.value })}
                          placeholder="Notas adicionais"
                          className="w-full rounded-lg border border-secondary-200 bg-white px-3 py-2 text-sm text-secondary-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-100"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleAddPiece}
                        disabled={isSavingItems || !newPieceItem.pieceDescription?.trim() || !newPieceItem.quantity || newPieceItem.quantity <= 0 || !newPieceItem.unitPrice}
                        className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSavingItems ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                        Adicionar
                      </button>
                      <button
                        onClick={() => {
                          setShowAddPiece(false)
                          setSelectedPieceCostId(null)
                          setNewPieceItem({ pieceDescription: '', quantity: 1, unitPrice: 0, notes: '' })
                        }}
                        className="flex items-center gap-2 rounded-lg bg-secondary-200 px-4 py-2 text-sm font-semibold text-secondary-700 transition-all hover:bg-secondary-300"
                      >
                        <X className="h-4 w-4" />
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}
              </CollapsibleSection>
            </div>
          )}

          {/* Orçamento final: serviços, peças, extra e total (manual ou calculado) */}
          {workOrder && (
            <div className="rounded-3xl border border-secondary-200 bg-gradient-to-br from-white to-secondary-50/30 p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 shadow-sm">
                  <FileText className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-secondary-900">Orçamento Final</h3>
                  <p className="text-xs text-secondary-500 mt-0.5">
                    Serviços, peças e custos adicionais (incluídos no PDF)
                  </p>
                </div>
              </div>
              <div className="border-t border-secondary-200 pt-4 space-y-3 text-sm">
                <div className="flex justify-between text-secondary-700">
                  <span>Serviços</span>
                  <span className="font-semibold">{budgetSummary.laborCost.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between text-secondary-700">
                  <span>Peças</span>
                  <span className="font-semibold">{budgetSummary.partsCost.toFixed(2)} €</span>
                </div>
                {budgetSummary.budgetCosts.map((cost) => (
                  <div key={cost.id ?? cost.description} className="flex justify-between items-center text-secondary-700 gap-3">
                    <span className="truncate">{cost.description}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="font-semibold">{(Number(cost.amount) || 0).toFixed(2)} €</span>
                      {canEditBudget && cost.id != null && (
                        <button
                          type="button"
                          onClick={() => void handleRemoveBudgetCost(cost.id!)}
                          disabled={isAddingBudgetCost}
                          className="rounded p-1 text-secondary-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                          aria-label={`Remover ${cost.description}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {canEditBudget ? (
                  showAddBudgetCost ? (
                    <div className="flex items-center gap-2 pt-1">
                      <input
                        type="text"
                        value={newBudgetCostDescription}
                        onChange={(e) => setNewBudgetCostDescription(e.target.value)}
                        placeholder="Descrição"
                        className="min-w-0 flex-1 rounded-lg border border-secondary-200 bg-white px-3 py-1.5 text-sm text-secondary-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                      />
                      <div className="relative w-28 shrink-0">
                        <input
                          type="number"
                          step="0.01"
                          value={newBudgetCostAmount}
                          onChange={(e) => setNewBudgetCostAmount(e.target.value)}
                          placeholder="Valor"
                          className="w-full rounded-lg border border-secondary-200 bg-white py-1.5 pl-3 pr-7 text-right text-sm font-semibold text-secondary-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-sm text-secondary-500">€</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => void handleAddBudgetCost()}
                        disabled={isAddingBudgetCost}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50"
                        aria-label="Adicionar custo"
                      >
                        {isAddingBudgetCost ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddBudgetCost(false)
                          setNewBudgetCostDescription('')
                          setNewBudgetCostAmount('')
                        }}
                        disabled={isAddingBudgetCost}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-secondary-200 text-secondary-600 hover:bg-secondary-50 disabled:opacity-50"
                        aria-label="Cancelar"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowAddBudgetCost(true)}
                      className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-secondary-300 bg-white px-3 py-2 text-sm font-semibold text-secondary-700 transition-colors hover:border-primary-400 hover:bg-primary-50/40 hover:text-primary-700"
                    >
                      <Plus className="h-4 w-4" />
                      Adicionar custo
                    </button>
                  )
                ) : (
                  budgetSummary.budgetCosts.length === 0 && budgetSummary.extraCostsTotal !== 0 && (
                    <div className="flex justify-between text-secondary-700">
                      <span>Extra</span>
                      <span className="font-semibold">{budgetSummary.extraCostsTotal.toFixed(2)} €</span>
                    </div>
                  )
                )}
                {budgetSummary.discount > 0 && (
                  <div className="flex justify-between text-secondary-600">
                    <span>Desconto</span>
                    <span className="font-semibold">-{budgetSummary.discount.toFixed(2)} €</span>
                  </div>
                )}
                {budgetSummary.tax > 0 && (
                  <div className="flex justify-between text-secondary-600">
                    <span>IVA</span>
                    <span className="font-semibold">{budgetSummary.tax.toFixed(2)} €</span>
                  </div>
                )}
                <div className="flex justify-between items-center text-base font-bold text-primary-600 pt-2 border-t border-secondary-200 gap-3">
                  <span>Total final</span>
                  <span>{budgetSummary.finalTotal.toFixed(2)} €</span>
                </div>
                {canEditBudget && (
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => {
                        const next = !applyVat
                        setApplyVat(next)
                        void saveTaxSettings(next, taxPercentage)
                      }}
                      className={cn(
                        'rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors',
                        applyVat
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-secondary-200 bg-white text-secondary-700 hover:border-secondary-300 hover:bg-secondary-50'
                      )}
                    >
                      Deseja adicionar IVA?
                    </button>
                    {applyVat && (
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={taxPercentage}
                          onChange={(e) => setTaxPercentage(parseBudgetNumber(e.target.value))}
                          onBlur={() => void saveTaxSettings()}
                          className="w-16 rounded-lg border border-secondary-200 bg-white px-2 py-1 text-right text-sm font-semibold text-secondary-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                          title="Percentagem de IVA"
                        />
                        <span className="text-sm font-semibold text-secondary-600">%</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}


            {/* Alterar Estado - Apenas para Mecânicos */}
            {(canActAsMechanic || canActAsReceptionist) && workOrder && (
              <div className="rounded-3xl border border-secondary-200 bg-gradient-to-br from-white to-secondary-50/30 p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 shadow-sm">
                    <CheckCircle2 className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-secondary-900">
                      {isScheduledWorkOrder ? 'Agendamento' : 'Alterar Estado'}
                    </h3>
                    <p className="text-xs text-secondary-500 mt-0.5">
                      Estado atual: <span className="font-semibold text-primary-600">{formatStatus(workOrder.status)}</span>
                    </p>
                  </div>
                </div>
              </div>
              {isScheduledWorkOrder ? (
                <div className="space-y-4">
                  <p className="text-sm text-secondary-800">O veículo já chegou à oficina?</p>
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => setShowArrivalConfirmModal(true)}
                      disabled={isConfirmingArrival || isCancellingScheduling}
                      className="rounded-xl bg-blue-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-600 disabled:opacity-50"
                    >
                      Sim
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelScheduling}
                      disabled={isConfirmingArrival || isCancellingScheduling}
                      className="rounded-xl border border-red-200 px-5 py-2.5 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                    >
                      {isCancellingScheduling ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Cancelar agendamento'
                      )}
                    </button>
                  </div>
                </div>
              ) : (
              <div className="space-y-3">
                {canCompleteWorkOrder && workOrder.status !== WorkOrderStatus.COMPLETED && (
                  <button
                    type="button"
                    data-testid="mark-completed-button"
                    onClick={handleMarkAsCompleted}
                    disabled={isChangingStatus}
                    className="w-full rounded-xl border-2 border-green-500 bg-green-50 px-4 py-3 text-sm font-semibold text-green-800 hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isChangingStatus ? (
                      <span className="inline-flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        A concluir...
                      </span>
                    ) : (
                      'Marcar como concluída'
                    )}
                  </button>
                )}
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <select
                      data-testid="status-change-select"
                      value={selectedNewStatus || workOrder.status}
                      onChange={(e) => setSelectedNewStatus(e.target.value as WorkOrderStatus)}
                      disabled={isChangingStatus || isWorkOrderEditingLocked(workOrder.status)}
                      className="w-full appearance-none rounded-xl border-2 border-secondary-200 bg-gradient-to-br from-white to-secondary-50/50 px-4 py-3.5 pr-12 text-sm font-semibold text-secondary-900 transition-all duration-200 focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-100 disabled:bg-secondary-50 disabled:text-secondary-500 disabled:cursor-not-allowed disabled:border-secondary-200 hover:border-primary-300 hover:shadow-sm cursor-pointer"
                    >
                      {availableStatuses.map((status) => (
                        <option key={status} value={status} className="bg-white text-secondary-900 font-medium">
                          {formatStatus(status)}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                      <ChevronDown className="h-5 w-5 text-secondary-400" />
                    </div>
                  </div>
                  <button
                    data-testid="status-change-submit-button"
                    onClick={handleStatusChange}
                    disabled={isChangingStatus || !selectedNewStatus || selectedNewStatus === workOrder.status || isWorkOrderEditingLocked(workOrder.status)}
                    className="px-6 py-3.5 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 text-sm font-semibold text-white shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-sm hover:from-primary-600 hover:to-primary-700"
                  >
                    {isChangingStatus ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      'Submeter'
                    )}
                  </button>
                </div>
                
                {/* Campo de observações */}
                <div className="space-y-2">
                  <div className="relative">
                    <input
                      type="text"
                      value={statusChangeNotes}
                      onChange={(e) => setStatusChangeNotes(e.target.value)}
                      placeholder="20 palavras ou menos"
                      maxLength={200}
                      disabled={isChangingStatus || isWorkOrderEditingLocked(workOrder.status)}
                      className="w-full rounded-lg border border-secondary-200 bg-white px-3 py-2 pr-16 text-xs text-secondary-900 placeholder:text-secondary-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100 disabled:bg-secondary-50 disabled:text-secondary-500 disabled:cursor-not-allowed"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <span className={cn(
                        "text-xs font-medium",
                        wordCount > 20 ? "text-red-500" : "text-secondary-400"
                      )}>
                        {wordCount}/20
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              )}
            </div>
            )}

            {/* Histórico de Alterações de Status */}
            {(canActAsMechanic || canActAsReceptionist) && workOrder && (
              <div className="rounded-3xl border border-secondary-200 bg-gradient-to-br from-white to-secondary-50/30 p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-secondary-500 to-secondary-600 shadow-sm">
                  <Clock className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-secondary-900">Histórico de Alterações</h3>
                  <p className="text-xs text-secondary-500 mt-0.5">
                    Registo completo de todas as alterações de status
                  </p>
                </div>
                {statusHistory.length > 0 && (
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700">
                    {statusHistory.length}
                  </div>
                )}
              </div>
              {statusHistory.length > 0 ? (
                <div className="max-h-[400px] overflow-x-auto overflow-y-auto pb-4">
                  <div className="flex items-start gap-2 min-w-max">
                    {/* Status mais recente (atual) - azul à esquerda */}
                    <div className="flex flex-col items-center">
                      <span className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-primary-500 to-primary-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm whitespace-nowrap">
                        {formatStatus(statusHistory[0].newStatus)}
                      </span>
                    </div>
                    
                    {/* Setas e status anteriores (do mais recente para o mais antigo) */}
                    {statusHistory.map((entry: any, index: number) => (
                      <div key={entry.id || index} className="flex items-start gap-2">
                        {/* Seta apontando para trás */}
                        <div className="flex flex-col items-center pt-1">
                          <ArrowLeft className="h-5 w-5 text-primary-500 flex-shrink-0" />
                          {/* Informações do usuário e data abaixo da seta */}
                          <div className="mt-2 flex flex-col items-center gap-1 min-w-[120px]">
                            <div className="flex items-center gap-1.5 rounded-lg bg-secondary-50 px-2 py-1 border border-secondary-100">
                              <UserIcon className="h-3 w-3 text-secondary-500" />
                              <span className="text-[10px] font-medium text-secondary-700 whitespace-nowrap">
                                {entry.changedByUserName || 'Sistema'}
                              </span>
                            </div>
                            <div className="flex items-center gap-1 text-secondary-400">
                              <Clock className="h-2.5 w-2.5" />
                              <span className="text-[10px] text-secondary-500 whitespace-nowrap">
                                {formatDate(entry.changedAt)}
                              </span>
                            </div>
                            {/* Notas se existirem */}
                            {entry.notes && entry.notes.trim() !== '' && (
                              <div className="mt-1 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100/50 border border-blue-200/50 p-2 max-w-[150px]">
                                <div className="flex items-start gap-1">
                                  <FileText className="h-2.5 w-2.5 text-blue-600 mt-0.5 flex-shrink-0" />
                                  <p className="text-[10px] text-blue-900 leading-tight font-medium break-words">
                                    {entry.notes}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Status anterior (oldStatus) */}
                        <div className="flex flex-col items-center">
                          <span className="inline-flex items-center gap-1.5 rounded-lg bg-secondary-100 px-3 py-1.5 text-xs font-semibold text-secondary-700 border border-secondary-200 shadow-sm whitespace-nowrap">
                            {formatStatus(entry.oldStatus)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="relative flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-secondary-100 to-secondary-200 mb-5 shadow-inner">
                    <Clock className="h-10 w-10 text-secondary-400" />
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/50 to-transparent" />
                  </div>
                  <p className="text-sm font-semibold text-secondary-700 mb-1.5">
                    Ainda não há alterações registadas
                  </p>
                  <p className="text-xs text-secondary-500 max-w-xs leading-relaxed">
                    O histórico de alterações aparecerá aqui quando o status da folha de obra for alterado
                  </p>
                </div>
              )}
              </div>
            )}
          </div>
        </div>
      </>

      {showWorkOrderAssistant && aiChatWorkOrderContext && (
        <>
          {isAssistantOpen && (
            <div
              className="fixed bottom-[4.75rem] right-3 z-50 flex w-[min(calc(100vw-1.5rem),24rem)] flex-col overflow-hidden rounded-2xl border border-secondary-200 bg-white shadow-2xl sm:right-4 sm:w-[min(calc(100vw-2rem),26rem)]"
              style={{ height: 'min(70vh, 560px)' }}
              role="dialog"
              aria-label="Assistente técnico"
            >
              <div className="flex shrink-0 items-center justify-between border-b border-secondary-100 px-4 py-3">
                <div>
                  <h2 className="text-sm font-semibold text-secondary-900">Assistente Técnico</h2>
                  <p className="text-xs text-secondary-500">
                    Pergunte sobre um passo a passo detalhado desta folha ou qualquer dúvida
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsAssistantOpen(false)}
                  className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-secondary-500 transition-colors hover:bg-secondary-100 hover:text-secondary-700"
                  aria-label="Fechar assistente"
                  title="Fechar (continua a processar em segundo plano)"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="min-h-0 flex-1">
                <AIChat className="h-full" workOrderContext={aiChatWorkOrderContext} />
              </div>
            </div>
          )}
          {!isAssistantOpen && (
            <div
              className="pointer-events-none fixed -left-[9999px] top-0 h-0 w-0 overflow-hidden opacity-0"
              aria-hidden="true"
            >
              <AIChat className="h-full min-h-[200px]" workOrderContext={aiChatWorkOrderContext} />
            </div>
          )}
          <div className="fixed bottom-3 right-3 z-50 sm:bottom-4 sm:right-4">
            {showAssistantHint && !isAssistantOpen && (
              <button
                type="button"
                onClick={() => setShowAssistantHint(false)}
                className="absolute bottom-[calc(100%+0.5rem)] right-1 w-[min(calc(100vw-5.5rem),14rem)] text-left transition-opacity duration-300"
                role="status"
                aria-live="polite"
              >
                <span className="relative block rounded-2xl rounded-br-md border border-secondary-200 bg-white px-3.5 py-2.5 text-xs leading-snug text-secondary-800 shadow-lg">
                  Se precisar de ajuda, o assistente técnico está aqui
                  <span
                    className="absolute -bottom-[7px] right-5 h-3.5 w-3.5 rotate-45 border-b border-r border-secondary-200 bg-white"
                    aria-hidden="true"
                  />
                </span>
              </button>
            )}
            <button
              type="button"
              data-testid="work-order-assistant-fab"
              onClick={() => {
                setShowAssistantHint(false)
                setIsAssistantOpen((open) => !open)
              }}
              className={cn(
                'flex min-h-[56px] min-w-[56px] items-center justify-center rounded-full text-white shadow-lg transition-all hover:scale-105 active:scale-95',
                isAssistantOpen
                  ? 'bg-secondary-700 hover:bg-secondary-800'
                  : 'bg-primary-600 hover:bg-primary-700'
              )}
              aria-label={isAssistantOpen ? 'Fechar assistente técnico' : 'Abrir assistente técnico'}
              title={isAssistantOpen ? 'Fechar assistente técnico' : 'Abrir assistente técnico'}
            >
              {isAssistantOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <MessageCircle className="h-6 w-6" />
              )}
            </button>
          </div>
        </>
      )}

      {/* Painel Lateral - Histórico do Cliente */}
      {/* Modal de pré-visualização do PDF */}
      {pdfPreviewUrl && workOrder && (
        <>
          <div
            className="fixed inset-0 bg-black/60 z-[110] transition-opacity"
            onClick={() => {
              window.URL.revokeObjectURL(pdfPreviewUrl)
              setPdfPreviewUrl(null)
            }}
          />
          <div className="fixed inset-4 sm:inset-8 md:inset-12 lg:inset-16 z-[111] flex flex-col bg-white rounded-xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-secondary-200 bg-secondary-50 shrink-0">
              <h3 className="text-sm font-semibold text-secondary-900">Pré-visualização — Folha {workOrder.workOrderNumber}</h3>
              <div className="flex items-center gap-2">
                <a
                  href={pdfPreviewUrl}
                  download={`Folha-${workOrder.id}.pdf`}
                  className="flex items-center gap-2 rounded-lg border border-primary-500 bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600"
                >
                  <Download className="h-4 w-4" />
                  Descarregar
                </a>
                <button
                  onClick={() => {
                    window.URL.revokeObjectURL(pdfPreviewUrl)
                    setPdfPreviewUrl(null)
                  }}
                  className="p-2 hover:bg-secondary-200 rounded-lg transition-colors"
                  aria-label="Fechar"
                >
                  <X className="h-5 w-5 text-secondary-700" />
                </button>
              </div>
            </div>
            <iframe
              src={pdfPreviewUrl}
              title="Pré-visualização da folha de obra"
              className="flex-1 w-full min-h-0 border-0"
            />
          </div>
        </>
      )}

      {showCustomerHistoryPanel && workOrder?.customerCompanyId && workOrder?.workshopCompanyId && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black/50 z-[100] transition-opacity"
            onClick={() => setShowCustomerHistoryPanel(false)}
          />
          {/* Painel Lateral */}
          <div className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl z-[100] transform transition-transform duration-300 ease-in-out overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-secondary-200 px-6 py-4 flex items-center justify-between z-10 shadow-sm">
              <div className="flex items-center gap-3">
                <History className="h-5 w-5 text-secondary-700" />
                <h2 className="text-lg font-semibold text-secondary-900">
                  Histórico do Cliente
                </h2>
              </div>
              <button
                onClick={() => setShowCustomerHistoryPanel(false)}
                className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
                aria-label="Fechar painel"
              >
                <X className="h-5 w-5 text-secondary-700" />
              </button>
            </div>
            <div className="p-6">
              <div className="mb-4 pb-4 border-b border-secondary-200">
                <p className="text-sm font-semibold text-secondary-900 mb-1">
                  {workOrder.customerCompanyName || 'Cliente'}
                </p>
                {(workOrder.customerEmail || workOrder.customerPhone) && (
                  <div className="mt-2 space-y-1">
                    {workOrder.customerEmail && (
                      <a
                        href={`mailto:${workOrder.customerEmail}`}
                        className="flex items-center gap-1.5 text-xs text-secondary-600 hover:text-primary-600"
                      >
                        <Mail className="h-3.5 w-3.5 shrink-0" />
                        <span>{workOrder.customerEmail}</span>
                      </a>
                    )}
                    {workOrder.customerPhone && (
                      <a
                        href={`tel:${workOrder.customerPhone}`}
                        className="flex items-center gap-1.5 text-xs text-secondary-600 hover:text-primary-600"
                      >
                        <Phone className="h-3.5 w-3.5 shrink-0" />
                        <span>{workOrder.customerPhone}</span>
                      </a>
                    )}
                  </div>
                )}
                {workOrder.vehicleRegistrationPlate && (
                  <p className="text-xs text-secondary-500">
                    Matrícula: {workOrder.vehicleRegistrationPlate}
                  </p>
                )}
              </div>
              <CustomerWorkOrderHistory
                customerCompanyId={workOrder.customerCompanyId}
                workshopCompanyId={workOrder.workshopCompanyId}
                currentWorkOrderId={workOrder.id}
              />
            </div>
          </div>
        </>
      )}
    </div>
  )
}

